import { hasMoreData } from 'v2/util/pagination';
import { DelegateMapEntry } from 'containers/Base/ActionDelegate';
import { Project } from 'data/terra/Project';
import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store/reducer';
import { ProjectSelectorState } from '../reducer';
import { PAGE_SIZE } from './FetchProjects';
import { uniqBy } from 'lodash';
import TerraBff from 'services/apis/terraBff/TerraBff';

enum ActionTypes {
  loadMore = 'projects/loadMore',
  loadMoreSuccess = 'projects/loadMoreSuccess',
  loadMoreFailure = 'projects/loadMoreFailure',
}

export interface FetchMoreProjectsAction {
  type: ActionTypes;
  nextProjects?: Project[];
  currentPage?: number;
  hasMore: boolean;
  error?: string;
}

export const fetchMoreProjects = (): ThunkAction<
  void,
  RootState,
  unknown,
  Action<string>
> => async (dispatch, getState) => {
  const {
    hasInitialized,
    hasMore,
    currentPage,
    isLoadingMore,
  } = getState().projectList;

  if (!hasInitialized || !hasMore || isLoadingMore) return;

  dispatch({ type: ActionTypes.loadMore });

  const currentOrganization = getState().organizationList.selectedOrganization;

  const nextPage = currentPage + 1;
  const result = await TerraBff.project.fetchProjects({
    organizationId: currentOrganization?.id,
    page: nextPage,
    pageSize: PAGE_SIZE,
  });
  if (result.isSuccess()) {
    const data = result.get()!.data;
    dispatch({
      type: ActionTypes.loadMoreSuccess,
      nextProjects: data.projects,
      currentPage: nextPage,
      hasMore: hasMoreData(data.pagination),
    });
  } else {
    dispatch({
      type: ActionTypes.loadMoreFailure,
      error: result.exception().message,
    });
  }
};

export const fetchMoreProjectsDelegate: DelegateMapEntry<
  FetchMoreProjectsAction,
  ProjectSelectorState
> = {
  [ActionTypes.loadMore]: (
    state: ProjectSelectorState,
    action: FetchMoreProjectsAction
  ): ProjectSelectorState => {
    return {
      ...state,
      isLoadingMore: true,
      loadMoreError: undefined,
    };
  },
  [ActionTypes.loadMoreSuccess]: (
    state: ProjectSelectorState,
    action: FetchMoreProjectsAction
  ): ProjectSelectorState => {
    const nextProjects = action.nextProjects ?? [];
    return {
      ...state,
      projects: uniqBy(
        [...state.projects, ...nextProjects],
        item => item.projectId
      ),
      currentPage: action.currentPage ?? 0,
      hasMore: action.hasMore,
      isLoadingMore: false,
    };
  },
  [ActionTypes.loadMoreFailure]: (
    state: ProjectSelectorState,
    action: FetchMoreProjectsAction
  ): ProjectSelectorState => {
    return {
      ...state,
      isLoadingMore: false,
      loadMoreError: action.error,
    };
  },
};
